import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-muted text-muted-foreground',
  pending_review: 'bg-yellow-500/20 text-yellow-700 dark:text-yellow-400',
  active: 'bg-green-600 text-white',
  paused: 'bg-orange-500 text-white',
  completed: 'bg-blue-500 text-white',
  cancelled: 'bg-destructive text-destructive-foreground',
};

export default function UGCCampaigns() {
  const queryClient = useQueryClient();
  const [selectedCampaign, setSelectedCampaign] = useState<any>(null);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectTarget, setRejectTarget] = useState<any>(null);
  const [rejectNotes, setRejectNotes] = useState('');

  const { data: campaigns, isLoading } = useQuery({
    queryKey: ['ugc-campaigns'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('brand_campaigns')
        .select('*, brand_configs(brand_name, category, contact_email)')
        .eq('campaign_type', 'ugc')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });

  const { data: appCounts } = useQuery({
    queryKey: ['ugc-campaign-app-counts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('campaign_applications')
        .select('campaign_id');
      if (error) throw error;
      const counts: Record<string, number> = {};
      (data ?? []).forEach((a: any) => {
        counts[a.campaign_id] = (counts[a.campaign_id] || 0) + 1;
      });
      return counts;
    },
  });

  const { data: applications } = useQuery({
    queryKey: ['ugc-campaign-applications', selectedCampaign?.id],
    enabled: !!selectedCampaign,
    queryFn: async () => {
      const { data } = await supabase
        .from('campaign_applications')
        .select('*')
        .eq('campaign_id', selectedCampaign.id)
        .order('applied_at', { ascending: false });
      return data ?? [];
    },
  });

  const { data: deliverables } = useQuery({
    queryKey: ['ugc-campaign-deliverables', selectedCampaign?.id],
    enabled: !!selectedCampaign,
    queryFn: async () => {
      const { data } = await supabase
        .from('campaign_deliverables')
        .select('*')
        .eq('campaign_id', selectedCampaign.id)
        .order('created_at', { ascending: false });
      return data ?? [];
    },
  });

  const updateStatus = useMutation({
    mutationFn: async ({ id, status, notes }: { id: string; status: string; notes?: string }) => {
      const update: any = { status, updated_at: new Date().toISOString() };
      if (status === 'active') update.admin_approved_at = new Date().toISOString();
      if (notes !== undefined) update.admin_notes = notes;
      const { error } = await supabase.from('brand_campaigns').update(update).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ugc-campaigns'] });
      toast.success('Campaign updated');
    },
  });

  const updateNotes = useMutation({
    mutationFn: async ({ id, notes }: { id: string; notes: string }) => {
      const { error } = await supabase.from('brand_campaigns').update({ admin_notes: notes }).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ugc-campaigns'] });
      toast.success('Notes saved');
    },
  });

  const sorted = [...(campaigns ?? [])].sort((a, b) => {
    if (a.status === 'pending_review' && b.status !== 'pending_review') return -1;
    if (b.status === 'pending_review' && a.status !== 'pending_review') return 1;
    return 0;
  });

  const pendingCount = (campaigns ?? []).filter((c: any) => c.status === 'pending_review').length;

  const handleApprove = (campaign: any) => {
    if (confirm('Approve this campaign and set it to active?')) {
      updateStatus.mutate({ id: campaign.id, status: 'active' });
    }
  };

  const handleReject = () => {
    if (rejectTarget) {
      updateStatus.mutate({ id: rejectTarget.id, status: 'cancelled', notes: rejectNotes });
      setRejectDialogOpen(false);
      setRejectTarget(null);
      setRejectNotes('');
    }
  };

  if (isLoading) return <div className="p-4 text-muted-foreground">Loading UGC campaigns...</div>;

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            UGC Campaigns
            {pendingCount > 0 && (
              <Badge className="bg-yellow-500/20 text-yellow-700">{pendingCount} pending</Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Brand</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Product</TableHead>
                  <TableHead>Platforms</TableHead>
                  <TableHead>Rate/Video</TableHead>
                  <TableHead>CPM</TableHead>
                  <TableHead>Videos</TableHead>
                  <TableHead>Usage Rights</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Apps</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sorted.map((c: any) => (
                  <TableRow key={c.id} className="cursor-pointer" onClick={() => setSelectedCampaign(c)}>
                    <TableCell className="font-medium">{c.brand_configs?.brand_name ?? '—'}</TableCell>
                    <TableCell>{c.title}</TableCell>
                    <TableCell>{c.product_name}</TableCell>
                    <TableCell>
                      <div className="flex gap-1 flex-wrap">
                        {(c.platforms ?? []).map((p: string) => (
                          <Badge key={p} variant="outline" className="text-xs">{p}</Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="whitespace-nowrap">${c.rate_per_video ?? 0}</TableCell>
                    <TableCell className="whitespace-nowrap">${c.cpm_rate ?? 0}</TableCell>
                    <TableCell>{c.num_videos}</TableCell>
                    <TableCell>
                      <Badge variant={c.usage_rights ? 'default' : 'outline'}>
                        {c.usage_rights ? 'Yes' : 'No'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={STATUS_COLORS[c.status] || ''}>{c.status}</Badge>
                    </TableCell>
                    <TableCell className="whitespace-nowrap">{appCounts?.[c.id] ?? 0}</TableCell>
                    <TableCell>{c.created_at ? formatDistanceToNow(new Date(c.created_at), { addSuffix: true }) : '—'}</TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <div className="flex gap-1">
                        {c.status === 'pending_review' && (
                          <>
                            <Button size="sm" variant="default" onClick={() => handleApprove(c)}>Approve</Button>
                            <Button size="sm" variant="destructive" onClick={() => { setRejectTarget(c); setRejectDialogOpen(true); }}>Reject</Button>
                          </>
                        )}
                        {c.status === 'active' && (
                          <Button size="sm" variant="outline" onClick={() => updateStatus.mutate({ id: c.id, status: 'paused' })}>Pause</Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {sorted.length === 0 && (
                  <TableRow><TableCell colSpan={12} className="text-center text-muted-foreground py-8">No UGC campaigns found</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Detail Sheet */}
      <Sheet open={!!selectedCampaign} onOpenChange={(open) => !open && setSelectedCampaign(null)}>
        <SheetContent className="sm:max-w-2xl overflow-y-auto">
          {selectedCampaign && (
            <>
              <SheetHeader>
                <SheetTitle>{selectedCampaign.title}</SheetTitle>
              </SheetHeader>
              <div className="space-y-6 mt-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><span className="text-muted-foreground">Brand:</span> {selectedCampaign.brand_configs?.brand_name}</div>
                  <div><span className="text-muted-foreground">Contact:</span> {selectedCampaign.brand_configs?.contact_email}</div>
                  <div><span className="text-muted-foreground">Product:</span> {selectedCampaign.product_name}</div>
                  <div><span className="text-muted-foreground">Rate/Video:</span> ${selectedCampaign.rate_per_video ?? 0}</div>
                  <div><span className="text-muted-foreground">CPM Rate:</span> ${selectedCampaign.cpm_rate ?? 0}</div>
                  <div><span className="text-muted-foreground">Videos:</span> {selectedCampaign.num_videos}</div>
                  <div><span className="text-muted-foreground">Usage Rights:</span> {selectedCampaign.usage_rights ? 'Yes' : 'No'}</div>
                  <div><span className="text-muted-foreground">Status:</span> <Badge className={STATUS_COLORS[selectedCampaign.status]}>{selectedCampaign.status}</Badge></div>
                </div>

                {selectedCampaign.description && (
                  <div>
                    <h4 className="font-medium mb-1">Description / Guidelines</h4>
                    <p className="text-sm text-muted-foreground whitespace-pre-wrap">{selectedCampaign.description}</p>
                  </div>
                )}

                <div>
                  <h4 className="font-medium mb-1">Admin Notes</h4>
                  <Textarea
                    defaultValue={selectedCampaign.admin_notes ?? ''}
                    onBlur={(e) => {
                      if (e.target.value !== (selectedCampaign.admin_notes ?? '')) {
                        updateNotes.mutate({ id: selectedCampaign.id, notes: e.target.value });
                      }
                    }}
                    placeholder="Add admin notes..."
                  />
                </div>

                {/* Applications */}
                <div>
                  <h4 className="font-medium mb-2">Applications ({applications?.length ?? 0})</h4>
                  {(applications ?? []).length > 0 ? (
                    <div className="space-y-2">
                      {applications!.map((a: any) => (
                        <div key={a.id} className="border rounded p-3 text-sm">
                          <div className="flex justify-between">
                            <span>TikTok: @{a.tiktok_handle} | IG: @{a.instagram_handle}</span>
                            <Badge className={STATUS_COLORS[a.status] || 'bg-muted'}>{a.status}</Badge>
                          </div>
                          <div className="text-muted-foreground mt-1">
                            Rate: ${a.proposed_rate} | TT followers: {a.tiktok_followers?.toLocaleString()} | IG followers: {a.instagram_followers?.toLocaleString()}
                          </div>
                          {a.pitch && <p className="mt-1 text-xs">{a.pitch}</p>}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No applications yet</p>
                  )}
                </div>

                {/* Deliverables */}
                <div>
                  <h4 className="font-medium mb-2">Deliverables ({deliverables?.length ?? 0})</h4>
                  {(deliverables ?? []).length > 0 ? (
                    <div className="space-y-2">
                      {deliverables!.map((d: any) => (
                        <div key={d.id} className="border rounded p-3 text-sm flex justify-between items-center">
                          <div>
                            <a href={d.video_url} target="_blank" rel="noreferrer" className="text-primary underline">{d.video_url}</a>
                            <div className="text-muted-foreground">{d.platform} · {d.views?.toLocaleString() ?? 0} views</div>
                          </div>
                          <Badge className={STATUS_COLORS[d.status] || 'bg-muted'}>{d.status}</Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No deliverables yet</p>
                  )}
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reject Campaign</DialogTitle></DialogHeader>
          <Textarea placeholder="Reason for rejection..." value={rejectNotes} onChange={(e) => setRejectNotes(e.target.value)} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleReject}>Reject</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
